'use client';

import {
  FC,
  FormEventHandler,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';
import { isPossiblePhoneNumber } from 'libphonenumber-js';

import { setEmailCookie } from '@monoimin/utils/server';
import { IS_DEV, RENTA_DOMAIN } from '@monoimin/constants';
import { Locales } from '@monoimin/sb-types';
import { useIntersectionObserver } from '@monoimin/hooks';
import { formInputsDict, gravityFormsDict } from '@monoimin/intl';
import {
  trackAmplitudeEvent,
  trackAmplitudeFormSubmittedError,
} from '@monoimin/analytics';
import {
  getAdsIds,
  getAnalyticsTrackingId,
  getRidAnmId,
  getTimezoneName,
  getTimezoneOffset,
} from '@monoimin/utils/analytics';
import { setAndReportValidity, defineDate } from '@monoimin/utils/helpers';
import { FormButton } from '@monoimin/atoms';
import { formatConsentValue } from '@monoimin/organisms/form/formatConsentValue';

import {
  createCollectionData,
  generateCollectionDataPayload,
  getEmailValidationData,
  getFieldCustomId,
  getInputName,
  logFormEvent,
} from './utils';
import { RenderInput } from './utils/render';
import { defaultFormEventsState } from './utils/constants';
import { mapAmplitudeAttributes } from './utils/amplitudeMapper';
import { isCountryBannerActive } from './utils/isCountryBannerActive';
import { formActionByInputName } from './constants';
import { customVariants } from './variants';

import {
  FORM_TYPE_MAP,
  FORM_UI_ELEMENT_MAP,
  FormRequestData,
  type CustomOnSubmit,
  type CustomVariantProps,
  type DataForCollection,
  type FormConfig,
  type FormEventsState,
  type FormLogEvent,
  type FormType,
  type FormValues,
  type FormValuesById,
  type FormZbFields,
  type LogDataPayload,
} from './types';

type SuccessMessage = string | null;

const initFormEventsState: FormEventsState = {
  ...defaultFormEventsState,
};

// флаг, чтобы отправить лог "Form loaded but invisible" только один раз при загрузке формы
let impressed = false;

/**
 * Компонент формы, используется для отрисовки и отправки формы, полученной из Gravity Forms;
 * Форма может содержать различные поля, такие как текстовые поля, радио-кнопки, чекбоксы, селекты и т.д.
 * Все поля формы настраиваются в админке WordPress, а затем получаются через API (см. функцию getFormConfigById в utils и пример конфига формы в mock-config.json).
 * Компонент также отправляет логи событий формы в ренту и newRelic.
 */
export const GravityForm = ({
  title,
  locale,
  classNames,
  formConfig,
  bottomBlok,
  id,
  customVariant,
  defaultSubmitText,
  onSubmitCallback,
  withoutDefaultStyle = false,
  buttonPosition = 'center',
  style = {
    maxWidth: '600px',
  },
  customVariantProps,
  initialSuccessMessage = null,
}: {
  title?: ReactNode;
  locale: Locales;
  classNames?: {
    form?: string;
    container?: string;
    button?: string;
  };
  formConfig: FormConfig;
  bottomBlok?: ReactNode;
  id?: string;
  customVariant?: keyof typeof customVariants;
  defaultSubmitText?: string;
  withoutDefaultStyle?: boolean;
  buttonPosition?: 'left' | 'center';
  style?: HTMLAttributes<HTMLFormElement>['style'];
  initialSuccessMessage?: SuccessMessage;
  customVariantProps?: Partial<CustomVariantProps>;
  onSubmitCallback?: () => void;
}) => {
  const dict = gravityFormsDict[locale];
  const formType = formConfig.title.split(' ')[0] as FormType;
  const formTypeName = FORM_TYPE_MAP[formType];
  const formClientSegment = formType == 'Partnership' ? 'Partner' : 'Client';
  const formTitle = formConfig.title;
  const formUiElement =
    FORM_UI_ELEMENT_MAP[formType as keyof typeof FORM_UI_ELEMENT_MAP] ||
    FORM_UI_ELEMENT_MAP.DEFAULT;

  if (!defaultSubmitText) {
    defaultSubmitText = dict.defaultButtonText;
  }

  const initFormValues = useMemo(() => {
    if (!formConfig || !formConfig?.fields?.length) {
      return {};
    }
    return formConfig.fields.reduce<Record<string, string>>(
      (acc, f) => ({ ...acc, [getInputName(f)]: f.defaultValue }),
      {}
    );
  }, [formConfig]);

  // объект значений формы, где ключ - id инпута из formConfig, значение - текущее значение этого поля
  const [formValuesById, setFormValuesById] = useState<FormValues>({});
  // объект значений формы, где ключ - name инпута в HTML форме (поля label или adminLabel из GF), значение - текущее значение этого поля
  const formValuesRef = useRef<FormValues>(initFormValues);

  // раньше на кнопке был лоадер, но его временно убрали, поэтому isLoading пока не используется
  const [isLoading, setLoading] = useState(false);

  const [successMessage, setSuccessMessage] = useState<string | null>(
    initialSuccessMessage
  );

  // объект состояния для событий формы, по умолчанию они все в статусе 'waiting', если событие произошло, то статус меняется на 'pending', если запрос отправлен, то на 'complete' или 'error'
  const [formEvents, setFormEvents] =
    useState<FormEventsState>(initFormEventsState);

  // реф на форму, используется для работы с нативными методами валидации формы
  const formRef = useRef<HTMLFormElement | null>(null);

  // функция для логирования событий формы
  const onFormLog = useCallback(
    async (event: FormLogEvent, rentaResponse?: string) => {
      if (formEvents[event] === 'waiting') {
        const prevState = { ...formEvents };

        setFormEvents({ ...prevState, [event]: 'pending' });
        const formValues = formValuesRef.current;
        const fr_form_id = String(formValues['fr_form_id']);

        const logData: LogDataPayload = {
          form_action: event,
          fr_form_id,
          fr_form_title: formTitle,
          language_form: locale,
          user_email: formValues['user_email'] as string,
          user_name: formValues['user_name'] as string,
          user_phone: formValues['user_phone'] as string,
        };

        // отправляем лог в ренту
        if (window.renta?.track) {
          window.renta.track('Form interactive', logData);
        } else {
          setTimeout(
            () => window.renta?.track('Form interactive', logData),
            1000
          );
        }

        const filteredEmptyFormValues = Object.entries(formValues).reduce<
          Record<string, string>
        >((acc, [key, value]) => {
          if (value) {
            return { ...acc, [key]: String(value) };
          }
          return acc;
        }, {});

        // отправляем лог в newRelic через роут /api/log
        const res = await logFormEvent({
          userAction: event,
          locale,
          rentaResponse,
          formId: fr_form_id,
          formTitle,
          filteredFormValues: filteredEmptyFormValues,
          message: 'Form interactive',
        });

        setFormEvents({
          ...prevState,
          [event]: res?.requestId || IS_DEV ? 'complete' : 'error',
        });
      }
    },
    [formEvents, formConfig, locale]
  );

  // используется только для события onFormLog('Form visible')
  const entry = useIntersectionObserver(formRef, {
    rootMargin: '-65px 0px 0px 0px',
  });
  const isFormIntersecting = !!entry?.isIntersecting;

  useEffect(() => {
    if (!impressed) {
      onFormLog('Form loaded but invisible');
      impressed = true;
    }
  }, []);

  useEffect(() => {
    if (isFormIntersecting) {
      onFormLog('Form visible');
    }
  }, [isFormIntersecting]);

  const onSubmit: CustomOnSubmit = async (event, isShowThankYou = true) => {
    try {
      event.preventDefault();

      if (!formRef.current || isLoading) return { success: false };

      onFormLog('Click on submit button');

      // проверяем все инпуты с типом select и с атрибутом required на валидность
      // берем все селекты с атрибутом required
      const selectInputs = Array.from(
        document.querySelectorAll('select[required]')
      );

      const invalidFields: string[] = [];

      // проверяем каждый селект на валидность
      const allSelectsValid = selectInputs?.length
        ? selectInputs.every((selectInput) => {
            const selectElement = selectInput as HTMLSelectElement;
            const isFieldsValid =
              selectElement.value !== formInputsDict[locale].select;
            if (!isFieldsValid) {
              invalidFields.push(selectElement.name);
            }
            return isFieldsValid;
          })
        : true;

      // нативный метод валидации инпутов, см. https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/checkValidity
      const isValid = formRef.current.checkValidity() && allSelectsValid;

      if (!isValid) {
        trackAmplitudeFormSubmittedError({
          error_type: 'input error',
          error_fields: invalidFields,
          form_client_segment: formClientSegment,
          form_ui_element: formUiElement,
          form_id: formConfig.id,
          form_type: formTypeName,
          form_name: formTitle,
        });

        formRef.current.reportValidity();
      }
      // проверяем каждый селект на валидность
      selectInputs.forEach((selectInput) => {
        const selectElement = selectInput as HTMLSelectElement;

        selectElement.setCustomValidity('');

        if (selectElement.value === formInputsDict[locale].select) {
          // если хотя бы один селект не валиден, то выкидываем ошибку
          // по default валидация селектов не работает т.к. там уже есть default value
          setAndReportValidity(selectElement, dict.enterValidOption);
        }
      });

      const phoneElem = document.querySelector(
        '[name=user_phone]'
      ) as HTMLInputElement | null;
      if (phoneElem) {
        const phoneValue = phoneElem.value.trim();
        if (
          !phoneValue ||
          phoneValue === '+' ||
          !isPossiblePhoneNumber(phoneValue)
        ) {
          setAndReportValidity(phoneElem, dict.invalidPhone);
          return { success: false };
        }
      }

      if (!isValid) {
        onFormLog('Form submitted failed');
        return { success: false };
      }

      const formData = new FormData(formRef.current);

      const entries = Array.from(formData.entries()).filter(
        ([inputName, inputValue]) =>
          !inputName.startsWith('_next_') && !(inputValue instanceof File)
      );
      const values = Object.fromEntries(entries);
      setLoading(true);

      // поле email в форме всегда имеет name=user_email, валидируем его отдельно, чтобы показать пользователю сообщение об ошибке, если он ввеел невалидный(несуществующий) email
      const emailElem = document.querySelector(
        '[name=user_email]'
      ) as HTMLInputElement | null;
      let validateEmailData: FormZbFields | null = null;
      // TODO: на KYC форме валидация email происходит каждый step, нужно оптимизировать
      if (emailElem) {
        // валидация email через сервис ZeroBounce
        validateEmailData = await getEmailValidationData(values['user_email']);

        if (!validateEmailData) {
          setLoading(false);
          setAndReportValidity(emailElem, dict.invalidEmail);
          if (window.clarity) {
            window.clarity('event', 'invalidEmail');
          }
          onFormLog('Email validation error');

          trackAmplitudeFormSubmittedError({
            error_type: 'input error',
            error_fields: ['user_email'],
            form_ui_element: formUiElement,
            form_type: formTypeName,
            form_name: formTitle,
            form_id: formConfig.id,
          });

          return { success: false };
        }
      }

      values['user_email'] = values['user_email']
        ? values['user_email'].toString().toLowerCase()
        : '';

      if (window.clarity) {
        window.clarity('event', 'validForm');
      }

      const pureHref = window.location.origin + window.location.pathname;
      const rid = getRidAnmId(); // рентовский анонимный id
      const adsIds = getAdsIds();
      const trackingId = getAnalyticsTrackingId() || '';
      const date = defineDate();
      const timezone = getTimezoneName();
      const timezoneOffset = getTimezoneOffset();

      const collectionDataPayload = await generateCollectionDataPayload(
        values,
        formConfig.title,
        locale,
        validateEmailData,
        pureHref,
        trackingId,
        date,
        timezone,
        timezoneOffset
      );

      const collectionData = await createCollectionData(
        pureHref,
        rid,
        trackingId,
        collectionDataPayload,
        date,
        adsIds
      );
      if (typeof values['user_email'] === 'string') {
        await setEmailCookie(values['user_email']);
      }

      const attributeFormData = mapAmplitudeAttributes(
        values,
        collectionDataPayload
      );

      const amplitudeFormName = values?.['kyc_b2b_companyname__c']
        ? 'Form KYC B2B Submitted'
        : values?.['kyc_last_name_c']
        ? 'Form KYC Client Submitted'
        : values?.['answer_1_customer_survey']
        ? 'Form Survey Submitted'
        : 'Form Submitted';

      trackAmplitudeEvent({
        deviceId: null,
        userId: rid,
        name: amplitudeFormName,
        properties: attributeFormData,
      });

      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
      }

      window.dataLayer?.push({
        event: 'formSubmission',
        name: amplitudeFormName,
        user_email: collectionDataPayload.user_email,
      });

      // отправляем в ренту напрямую, а не через window.analytics, потому что window.analytics не всегда доступен (некоторые браузеры блокируют трекинг)
      await fetch(`https://${RENTA_DOMAIN}/collect/v3/t`, {
        method: 'POST',
        body: JSON.stringify(collectionData),
        headers: {
          Cookie: `_rid=${rid}; _rid_anm_id=${rid}`,
          'Content-Type': 'application/json',
        },
      })
        .then((res) => {
          if (IS_DEV) return 'success';
          fetch('/api/log/', {
            method: 'POST',
            body: JSON.stringify({
              attributes: collectionData,
              status: res.status || null,
              message: 'Renta response',
            }),
          });
          return res.status === 200 ? 'success' : 'failure';
        })
        .catch(async (error) => {
          fetch('/api/log/', {
            method: 'POST',
            body: JSON.stringify({
              attributes: collectionData,
              errorMessage: error.message || null,
              message: 'Renta error',
            }),
          });

          trackAmplitudeFormSubmittedError({
            error_type: 'input error',
            error_fields: invalidFields,
            error_status_code: error.status,
            form_client_segment: formClientSegment,
            form_ui_element: formUiElement,
            form_type: formTypeName,
            form_name: formTitle,
            form_id: formConfig.id,
          });

          return 'failure';
        });

      // messageId это reserved name в newRelic, поэтому меняем его на messageIdV2
      const newRelicCollectionData: DataForCollection & {
        messageIdV2: string;
      } = {
        ...collectionData,
        messageIdV2: collectionData.messageId,
      };

      // отправляем эту форму в newRelic
      fetch('/api/log/', {
        method: 'POST',
        body: JSON.stringify({
          attributes: newRelicCollectionData,
          message: 'Form submitted',
        }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .catch((err) => err);

      // отправляем эту форму в базу данных BigQuery
      await fetch('/api/submit/', {
        method: 'POST',
        body: JSON.stringify({
          ...collectionData,
          user_cookie_consent: formatConsentValue(
            (collectionData.properties as FormRequestData)?.user_cookie_consent
          ),
        }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .catch((error) => {
          fetch('/api/log/', {
            method: 'POST',
            body: JSON.stringify({
              attributes: {
                location: pureHref,
                path: window.location.pathname,
                page: pureHref,
                documentReferrer: pureHref,
                deviceUserAgent: window.navigator.userAgent,
                errorMessage: error?.message || null,
                collectionData,
              },
              message: 'BQ Client Error',
            }),
          });
        })
        .then((res) => console.log(`submit route res`, res));

      // @ts-expect-error not exists
      if (typeof window.fbq === 'function') {
        // @ts-expect-error not exists
        window.fbq('track', 'Lead', {
          ...collectionDataPayload,
          user_email: '',
          user_phone: '',
          user_name: '',
        });

        // @ts-expect-error not exists
        window.fbq('track', 'Form send', {
          ...collectionDataPayload,
          user_email: '',
          user_phone: '',
          user_name: '',
        });
      }

      if (window.clarity) {
        window.clarity('event', 'formSubmitted');
      }

      onFormLog('Form submitted success');

      setLoading(false);

      const confirmationConfig = Object.values(formConfig.confirmations)[0];
      // есть два варианта того, как мы показываем пользователю подтверждение отправки формы: редирект на страницу /thank-you-... или показ сообщения на текущей странице
      const isConfirmationRedirect = confirmationConfig?.type === 'redirect';
      const isConfirmationMessage = confirmationConfig?.type === 'message';

      const showThankYou = () => {
        if (isConfirmationMessage) {
          setSuccessMessage(confirmationConfig.message);
        }

        // если форма отправлена успешно и есть редирект, то редиректим пользователя на страницу благодарности
        if (isConfirmationRedirect) {
          const thankYouPage = new URL(confirmationConfig.url).pathname;

          // добалвяем email в query параметры, чтобы потом его использовать на странице благодарности
          window.location.href = `https://immigrantinvest.com${thankYouPage}`;
        }
      };

      if (isShowThankYou) {
        showThankYou();
      }
      onSubmitCallback?.();
      return { success: true, showThankYou };
    } catch (error: any) {
      console.error(`onSubmit`, error);

      const pureHref = window.location.origin + window.location.pathname;

      fetch('/api/log/', {
        method: 'POST',
        body: JSON.stringify({
          attributes: {
            location: pureHref,
            path: window.location.pathname,
            page: pureHref,
            documentReferrer: pureHref,
            deviceUserAgent: window.navigator.userAgent,
            errorMessage: error?.message || null,
            formValuesById,
          },
          message: 'Form error',
        }),
      });
      return { success: false };
    }
  };

  const onInput = useCallback<FormEventHandler<HTMLFormElement>>(
    (event) => {
      const formData = new FormData(event.currentTarget);
      const values = Object.fromEntries(formData.entries());
      formValuesRef.current = values;

      const newFormValuesById = formConfig.fields.reduce<FormValuesById>(
        (acc, f) => {
          const inputId = getFieldCustomId(f);

          const inputEl = document.querySelector(
            `#${inputId}`
          ) as HTMLInputElement | null;
          const inputName = inputEl?.name || getInputName(f);

          if (f.type === 'fileupload') {
            // файловые инпуты обрабатываю отдельно, потому что при изменении любого инпута в форме (даже просто текстового) почему-то добавляется пустой объект File в инпут
            const inputFile = values[inputName] as File | undefined;
            return {
              ...acc,
              [f.id]: inputFile?.['name'] ? values[inputName] : undefined,
            };
          }
          return { ...acc, [f.id]: values[inputName] };
        },
        {}
      );

      setFormValuesById(newFormValuesById);

      const input = event.target as HTMLInputElement;
      const inputName = input.name;
      const inputType = input.type;

      const action = formActionByInputName[inputName];

      const isTextInput =
        inputType === 'text' || inputType === 'email' || inputType === 'tel';
      const logInput = isTextInput ? input.value?.length > 3 : true;

      if (action && logInput) {
        onFormLog(action);
      }
    },
    [onFormLog]
  );

  if (customVariant) {
    // Don't render SubscribePopup when a country-specific banner is active
    if (customVariant === 'SubscribePopup' && isCountryBannerActive()) {
      return null;
    }

    const CustomComponent = customVariants[
      customVariant
    ] as FC<CustomVariantProps>;

    if (!CustomComponent) return null;

    return (
      <CustomComponent
        locale={locale}
        formConfig={formConfig}
        onInput={onInput}
        onSubmit={onSubmit}
        formValues={formValuesRef.current}
        formValuesById={formValuesById}
        formRef={formRef}
        successMessage={successMessage}
        isLoading={isLoading}
        {...customVariantProps}
      />
    );
  }

  const containerClassName = clsx(
    !withoutDefaultStyle && 'gravity-form flex flex-col h-full',
    classNames?.container
  );

  if (successMessage) {
    return (
      <div id={id} className={containerClassName}>
        {title}
        <div
          dangerouslySetInnerHTML={{
            __html: successMessage,
          }}
          className='gravity-form-message text-xl [&_h3]:text-2xl [&_h3]:font-bold [&_h3]:mb-2 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:mb-2 [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:mb-2'
        />
      </div>
    );
  }

  return (
    <div id={id} className={containerClassName}>
      {title}
      <form
        ref={formRef}
        onSubmit={(e) => onSubmit(e)}
        onInput={onInput}
        style={style}
        className={clsx(
          'gf-next w-full grid grid-cols-12 gap-x-6 gap-4',
          classNames?.form
        )}
      >
        {formConfig.fields.map((field) => {
          const customId = getFieldCustomId(field);
          return (
            <RenderInput
              key={field.id}
              {...field}
              customId={customId}
              formValues={formValuesRef.current}
              formValuesById={formValuesById}
              locale={locale}
              classNames={{
                container: clsx({
                  'mb-3': field.type === 'select',
                }),
              }}
            />
          );
        })}
        <FormButton
          type='submit'
          loading={isLoading}
          dataTestId='gravity-form-submit-button'
          className={clsx(
            'gravity-form-submit-button mt-4 w-full md:w-fit',
            classNames?.button,
            {
              'col-span-12': buttonPosition === 'center',
              'max-md:col-span-12': buttonPosition === 'left',
            }
          )}
        >
          {formConfig.button.text || defaultSubmitText}
        </FormButton>
        {bottomBlok}
      </form>
    </div>
  );
};
